/* eslint-disable react-hooks/rules-of-hooks */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { defineNextPage } from '@/common/next';
import Head from 'next/head';
import { isAssetLibraryApiMessageShown, checkEditImagePrice } from '$/api/canvasRquest';
import { TaskCategory } from '$/api/server.types';
import Button from '$/components/base/Button';
import IconFont from '$/components/IconFont';
import ImageUploader from '@/components/ImageUploader';
import { message } from '$/components/Message';
import useDisableWheelZoom from '$/hooks/useDisableWheelZoom';
import useImageUpload from '@/hooks/useImageUpload';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getUrlSearchParams } from '$/utils';
import { downloadImageUrl } from '$/utils/download';
import { downloadImages } from '$/utils/export';
import indexedDBHelper from '@/utils/indexedDBHelper';
import { FileTypeEnum, urlToBlob } from '$/utils/upload';
import AIToolsHeader from '@/components/AITools/AIToolsHeader';
import useAIEnhanceStore, {
  setBatchProcessingMode,
  cancelBatchSelection,
  setBatchImages,
  setPreviewImageIndex,
  triggerBatchReupload,
  triggerSingleReupload,
  setBatchReuploadPending,
  setMode,
  setSingleOriginalImage,
  addBatchImages,
  setSingleCompareIng,
  setSingleResultImage,
  setSingleMediaType,
  setSingleVideoDuration,
  setSingleVideoRequestDuration,
  setSingleEraserLoading,
  resetAIEnhanceStore,
} from '@/store/useAIEnhanceStore';
import { handleFitScreen } from '@/store/useAIToolsCanvasStore';
import { useShallow } from 'zustand/react/shallow';
import { setEditImagePrice } from '$/store/useAIEditImageStore';
import AIToolsInfiniteCanvas, { type MediaItem } from '@/components/AITools/InfiniteCanvas';
import CanvasToolbar from '@/components/AITools/CanvasToolbar';
import { BatchCenterContent } from '@/components/AITools/BatchHeaderContent';
import { replaceTemplateParams } from '@/utils';
import LeftPanel from './components/LeftPanel';
import BatchList from './components/BatchList';
import BatchImagePreview from './components/BatchImagePreview';
import VideoBar from './components/VideoBar';
import {
  createUploadedMediaBatchItem,
  filterUploadedByMediacheck,
  getUploadedVideoDurationInfo,
} from '../ai_eraser/utils/mediaCheckAfterUpload';
import {
  AssetToolType,
  addAiToolResultsToAssetLibrary,
  autoSaveAiToolUploadedAssets,
} from '../ai_eraser/utils/assetLibrary';
import { isImageFile, isVideoFile } from '../ai_eraser/utils/mediaType';
import { createOpenMediaBatchItem, getOpenMediaType, getOpenVideoDurationInfo } from '../ai_eraser/utils/openMedia';
import {
  ensureAiToolUploadLoginAsync,
  useAiToolUploadLoginGuard,
} from '../utils/uploadLoginGuard';
import {
  joinAiToolTrackContentTypes,
  trackAiToolLandingPageEnter,
  trackAiToolDownloadSuccess,
  trackAiToolOpenWorkflow,
  trackAiToolUploadClick,
  trackAiToolUploadSuccess,
} from '../utils/aiToolUploadTrack';
import useOpenAiToolResultInTeamStudio from '../utils/openAiToolResultInTeamStudio';
import { getBatchEnhanceTrackParams, getEnhanceTrackParams } from './utils/enhanceTrack';
import styles from './index.module.scss';

/** 图片格式 */
const IMAGE_ACCEPT = '.png,.jpg,.jpeg';
/** 视频格式 */
const VIDEO_ACCEPT = '.mp4,.mov,.m4v';
/** 全部支持格式 */
const ALL_ACCEPT = `${IMAGE_ACCEPT},${VIDEO_ACCEPT}`;
const UPLOAD_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'video/mp4', 'video/quicktime'];
/** 图片大小限制 30MB */
const IMAGE_MAX_SIZE = 30 * 1024 * 1024;
/** 视频大小限制 500MB */
const VIDEO_MAX_SIZE = 500 * 1024 * 1024;
/** 最大文件数 */
const MAX_FILE_COUNT = 30;

/** 单张模式：只校验第一个文件的大小与格式 */
function validateFiles(files: File[]): File[] {
  const file = files[0];
  if (!file) return [];

  if (isVideoFile(file)) {
    if (file.size > VIDEO_MAX_SIZE) {
      message.warning(replaceTemplateParams(
        useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.video_exceeds_500mb_limit_skipped),
        { name: file.name },
      ));
      return [];
    }
    return [file];
  }
  if (isImageFile(file)) {
    if (file.size > IMAGE_MAX_SIZE) {
      message.warning(replaceTemplateParams(
        useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.picture_exceeds_30mb_limit),
        { picture_name: file.name },
      ));
      return [];
    }
    return [file];
  }
  return [];
}

export default defineNextPage<{
  title: string;
}>({
  pageConfig: {
    projectId: '/--roboneo',
    layoutId: 'RoboneoLayout',
    renderHead({ pageProps }) {
      return (
        <Head>
          <title>RoboNeo</title>
        </Head>
      );
    },
  },
  component: () => {
    const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
    const guardUploadEntry = useAiToolUploadLoginGuard();
    const languageStore = useLanguageStore((state) => state.languageStore);
    const { openInTeamStudio } = useOpenAiToolResultInTeamStudio();
    const {
      singleOriginalImage,
      singleResultImage,
      singleResultTaskId,
      mode,
      batchImages,
      batchMaxCount,
      batchProcessingMode,
      previewImageIndex,
      singleCompareIng,
      singleReuploadSignal,
      singleEraserLoading,
      batchEraserLoading,
      singleMediaType,
      imageHdType,
      videoSRMode,
    } = useAIEnhanceStore(
      useShallow((state) => ({
        singleCompareIng: state.singleCompareIng,
        previewImageIndex: state.previewImageIndex,
        singleResultImage: state.singleResultImage,
        singleResultTaskId: state.singleResultTaskId,
        singleOriginalImage: state.singleOriginalImage,
        mode: state.mode,
        batchImages: state.batchImages,
        batchMaxCount: state.batchMaxCount,
        batchProcessingMode: state.batchProcessingMode,
        singleReuploadSignal: state.singleReuploadSignal,
        singleEraserLoading: state.singleEraserLoading,
        batchEraserLoading: state.batchEraserLoading,
        singleMediaType: state.singleMediaType,
        imageHdType: state.imageHdType,
        videoSRMode: state.videoSRMode,
      })),
    );

    const selectedCount = batchImages.filter((img) => img.selected).length;
    const isPreviewMode = previewImageIndex !== null && mode === 'batch';
    const [isDownloading, setIsDownloading] = useState(false);
    const [isAddingAsset, setIsAddingAsset] = useState(false);
    useDisableWheelZoom();
    const { uploadImages: uploadByFileKey, uploading } = useImageUpload({
      maxCount: MAX_FILE_COUNT,
      acceptedTypes: UPLOAD_ACCEPTED_TYPES,
      maxFileSize: (file) => (isVideoFile(file) ? 500 : 30),
      afterUpload: async (images, files) => {
        const safeImages = await filterUploadedByMediacheck(images, files, { isCheckSafe: true });
        autoSaveAiToolUploadedAssets(safeImages, files, ({ materialType }) => (
          materialType === 'video' ? AssetToolType.VIDEO_HD : AssetToolType.IMAGE_UPSCALER
        ));
        return safeImages;
      },
      onError: (err) => {
        console.error('上传失败:', err);
      },
    });

    const triggeredFileKeyRef = useRef<string | null>(null);
    const triggeredOpenUrlRef = useRef<string | null>(null);
    const singleUploadTrackSourceRef = useRef<'reload' | 'canvas' | null>(null);
    const uploadByFileKeyRef = useRef(uploadByFileKey);
    uploadByFileKeyRef.current = uploadByFileKey;

    useEffect(() => {
      trackAiToolLandingPageEnter('change_to_hd');
    }, []);

    /**
     * 单图上传入口打开文件选择框时立即上报点击，并保留来源给成功埋点复用。
     */
    const handleSingleUploadOpen = useCallback(() => {
      if (!singleOriginalImage && !singleUploadTrackSourceRef.current) {
        singleUploadTrackSourceRef.current = 'canvas';
      }
      const uploadTrackSource = singleUploadTrackSourceRef.current;
      if (uploadTrackSource) {
        trackAiToolUploadClick({
          func: 'change_to_hd',
          source: uploadTrackSource,
        });
      }
    }, [singleOriginalImage]);

    /**
     * 单图上传成功后写入画布，并用真实文件补齐成功埋点字段。
     */
    const handleSingleUpload = useCallback(async (files: File[]) => {
      if (!guardUploadEntry()) return;
      const uploadTrackSource = singleUploadTrackSourceRef.current;
      singleUploadTrackSourceRef.current = null;
      setSingleOriginalImage('');
      setSingleResultImage('');
      setSingleVideoDuration(undefined);
      setSingleVideoRequestDuration(undefined);

      const validFiles = validateFiles(files);
      if (!validFiles.length) return;

      const file = validFiles[0];
      // 判断上传的是图片还是视频
      setSingleMediaType(isVideoFile(file) ? 'video' : 'image');

      const uploaded = await uploadByFileKeyRef.current(validFiles);
      if (!uploaded?.[0]?.url) return;
      const videoDurationInfo = await getUploadedVideoDurationInfo(uploaded[0], validFiles, 0);
      if (uploadTrackSource) {
        trackAiToolUploadSuccess({
          func: 'change_to_hd',
          source: uploadTrackSource,
          file,
        });
      }
      setSingleVideoDuration(videoDurationInfo.duration);
      setSingleVideoRequestDuration(videoDurationInfo.requestDuration);
      setSingleOriginalImage(uploaded[0].url);
    }, [guardUploadEntry]);

    useEffect(() => {
      const searchParams = getUrlSearchParams();
      const fileKey = searchParams.get('_filekey_');
      const openUrl = searchParams.get('open');
      const modeType = searchParams.get('type');
      if (modeType) {
        setMode(modeType === 'batch' ? 'batch' : 'single');
      }
      if (!fileKey && openUrl && triggeredOpenUrlRef.current !== openUrl) {
        triggeredOpenUrlRef.current = openUrl;
        (async () => {
          if (!await ensureAiToolUploadLoginAsync()) return;
          const openMediaType = getOpenMediaType(openUrl);
          trackAiToolUploadClick({
            func: 'change_to_hd',
            source: 'loading_page',
            url: openUrl,
            contentType: openMediaType,
          });
          if (modeType === 'batch') {
            setBatchProcessingMode(false);
            cancelBatchSelection();
            setPreviewImageIndex(null);
            setBatchImages([await createOpenMediaBatchItem(openUrl)]);
          } else {
            const durationInfo = await getOpenVideoDurationInfo(openUrl);
            setSingleResultImage('');
            setSingleVideoDuration(durationInfo.duration);
            setSingleVideoRequestDuration(durationInfo.requestDuration);
            setSingleMediaType(openMediaType);
            setSingleOriginalImage(openUrl);
          }
          trackAiToolUploadSuccess({
            func: 'change_to_hd',
            source: 'loading_page',
            url: openUrl,
            contentType: openMediaType,
          });
          const url = new URL(window.location.href);
          url.searchParams.delete('open');
          window.history.replaceState({}, '', url.pathname + url.search);
        })();
        return;
      }
      if (!fileKey || triggeredFileKeyRef.current === fileKey) return;

      triggeredFileKeyRef.current = fileKey;

      (async () => {
        try {
          if (!await ensureAiToolUploadLoginAsync()) return;
          const files = await indexedDBHelper.getUploadFiles(fileKey);
          if (!files.length) {
            console.error('IndexedDB 中未找到对应文件，fileKey:', fileKey);
            return;
          }
          if (modeType === 'batch') {
            setBatchProcessingMode(false);
            cancelBatchSelection();
            setPreviewImageIndex(null);
            setBatchImages([]);
            const uploaded = await uploadByFileKeyRef.current(files);
            if (uploaded?.length) {
              const newItems = await Promise.all(
                uploaded.map((img, i) => createUploadedMediaBatchItem(
                  img,
                  files,
                  i,
                  `upload-${Date.now()}-${i}`,
                )),
              );
              if (newItems.length) addBatchImages(newItems);
            }
          } else {
            await handleSingleUpload(files);
          }

          await indexedDBHelper.delete(fileKey);
          const url = new URL(window.location.href);
          url.searchParams.delete('_filekey_');
          window.history.replaceState({}, '', url.pathname + url.search);
        } catch (err) {
          console.error('从 IndexedDB 读取并上传失败:', err);
        }
      })();
    }, [handleSingleUpload]);

    useEffect(() => () => {
      resetAIEnhanceStore();
    }, []);

    // 登录状态下，查询编辑图片价格
    useEffect(() => {
      const fetchEditImagePrice = async () => {
        if (isUserLoggedIn) {
          const data = await checkEditImagePrice();

          let priceResult = {} as Record<TaskCategory, number>;
          data.web_items.forEach((item) => {
            priceResult = { ...priceResult, ...item };
          });
          setEditImagePrice(priceResult);
        }
      };
      fetchEditImagePrice();
    }, [isUserLoggedIn]);

    const renderCenterContent = () => {
      if (mode !== 'batch') return undefined;
      if (isPreviewMode) return undefined;
      if (batchImages.length < 2) return undefined;
      return (
        <BatchCenterContent
          count={batchImages.length}
          total={batchMaxCount}
          isProcessingMode={batchProcessingMode}
          selectedCount={selectedCount}
          showVipBadge
        />
      );
    };

    const handleReUpload = () => {
      if (!guardUploadEntry()) return;

      if (mode === 'batch') {
        setBatchProcessingMode(false);
        cancelBatchSelection();
        setPreviewImageIndex(null);
        setBatchReuploadPending(true);
        triggerBatchReupload();
      }

      if (mode === 'single') {
        singleUploadTrackSourceRef.current = 'reload';
        triggerSingleReupload();
      }
    };

    /**
     * 在工作流中打开，并上报顶部按钮点击埋点。
     */
    const openInWorkflow = () => {
      if (singleEraserLoading || batchEraserLoading) return;
      if (mode === 'single') {
        const type = singleMediaType === 'video' ? 'video' : 'image';
        trackAiToolOpenWorkflow({
          func: 'change_to_hd',
          taskIds: [singleResultTaskId],
          urlList: [singleResultImage],
        });
        openInTeamStudio([{ url: singleResultImage, type }]);
        return;
      }
      const selectedImages = batchImages.filter((img) => img.selected);
      if (selectedImages.length === 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.plz_pick));
        return;
      }
      const initMediaList = selectedImages.map((img) => ({
        url: img.resultUrl || img.url,
        type: img.type === FileTypeEnum.Video ? 'video' as const : 'image' as const,
        width: img.width,
        height: img.height,
        name: img.fileName,
        coverUrl: img.coverUrl,
      }));
      trackAiToolOpenWorkflow({
        func: 'change_to_hd',
        taskIds: selectedImages.map((img) => img.taskId),
        urlList: initMediaList.map((img) => img.url),
      });
      openInTeamStudio(initMediaList);
    };

    /**
     * 切换结果对比状态；生成中禁止用旧结果进入对比。
     */
    const handleCompare = () => {
      if (singleEraserLoading || batchEraserLoading) return;
      const { singleCompareIng: currentSingleCompareIng } = useAIEnhanceStore.getState();
      setSingleCompareIng(!currentSingleCompareIng);
    };

    const downloadHandler = async () => {
      if (isDownloading) return;
      setIsDownloading(true);
      try {
        if (mode === 'single') {
          await downloadImageUrl({ url: singleResultImage });
          trackAiToolDownloadSuccess({
            func: 'change_to_hd',
            taskId: singleResultTaskId,
            saveUrl: singleResultImage,
            contentType: singleMediaType === 'video' ? 'video' : 'image',
            generateType: 1,
            ...getEnhanceTrackParams({
              mediaType: singleMediaType,
              imageHdType,
              videoSRMode,
            }),
            downMode: 1,
            number: 1,
          });
          return;
        }

        // 大图预览模式下：下载当前预览的那一张
        if (isPreviewMode && previewImageIndex !== null) {
          const current = batchImages[previewImageIndex];
          if (current) {
            const url = current.resultUrl || current.url;
            await downloadImageUrl({ url });
            trackAiToolDownloadSuccess({
              func: 'change_to_hd',
              taskId: current.taskId,
              saveUrl: url,
              contentType: current.type === FileTypeEnum.Video ? 'video' : 'image',
              generateType: 2,
              ...getEnhanceTrackParams({
                mediaType: current.type || FileTypeEnum.Image,
                imageHdType,
                videoSRMode,
              }),
              downMode: 1,
              number: 1,
            });
          }
          return;
        }

        const selectedImages = batchImages.filter((img) => img.selected);
        if (selectedImages.length === 0) return;
        const urls = selectedImages.map((img) => img.resultUrl || img.url);
        const blobs = await Promise.all(urls.map((url) => urlToBlob(url)));
        const exportData = blobs.map((blob, i) => {
          const item = selectedImages[i];
          const fileName = item?.fileName || `${item?.type === FileTypeEnum.Video ? 'video' : 'image'}-${i + 1}`;
          return {
            name: `${fileName}.${blob.type.split('/')[1] || 'png'}`,
            blob,
          };
        });
        await downloadImages(
          exportData,
          {},
          {
            createZipName: () => `RoboNeo-${Date.now()}`,
          },
        );
        const batchContentType = joinAiToolTrackContentTypes(
          selectedImages.map((img) => (img.type === FileTypeEnum.Video ? 'video' : 'image')),
        );
        trackAiToolDownloadSuccess({
          func: 'change_to_hd',
          taskIds: selectedImages.map((img) => img.taskId),
          saveUrls: urls,
          contentType: batchContentType,
          generateType: 2,
          ...getBatchEnhanceTrackParams({
            items: selectedImages,
            imageHdType,
            videoSRMode,
          }),
          downMode: selectedImages.length > 1 ? 2 : 1,
          number: selectedImages.length,
        });
      } finally {
        setIsDownloading(false);
      }
    };

    /**
     * 点击后将当前高清结果加入资产库，批量预览时取当前预览图，批量处理态取已选图。
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleAddToAssetLibrary = async () => {
      if (isAddingAsset) return;
      setIsAddingAsset(true);
      try {
        const targets = (() => {
          if (mode === 'single') {
            return [{
              url: singleResultImage,
              taskId: singleResultTaskId,
              materialType: singleMediaType === 'video' ? 'video' as const : 'image' as const,
            }];
          }
          if (isPreviewMode && previewImageIndex !== null) {
            const current = batchImages[previewImageIndex];
            return current
              ? [{
                url: current.resultUrl || current.url,
                taskId: current.taskId,
                materialType: current.type === FileTypeEnum.Video ? 'video' as const : 'image' as const,
                width: current.width,
                height: current.height,
                duration: current.duration,
                coverUrl: current.coverUrl,
                name: current.fileName,
              }]
              : [];
          }
          return batchImages
            .filter((img) => img.selected)
            .map((img) => ({
              url: img.resultUrl || img.url,
              taskId: img.taskId,
              materialType: img.type === FileTypeEnum.Video ? 'video' as const : 'image' as const,
              width: img.width,
              height: img.height,
              duration: img.duration,
              coverUrl: img.coverUrl,
              name: img.fileName,
            }));
        })();
        const assetIds = await addAiToolResultsToAssetLibrary(targets, 'enhance');
        if (!assetIds.length) {
          message.info('当前暂无可添加到资产库的内容');
          return;
        }
        message.success('roboneo_web_saved_successfully');
      } catch (error) {
        console.error('[ai_enhance] add result to asset library failed', error);
        if (!isAssetLibraryApiMessageShown(error)) {
          message.error('添加到资产库失败');
        }
      } finally {
        setIsAddingAsset(false);
      }
    };

    /**
     * 渲染顶部右侧操作区；生成期间旧结果仍存在，操作按钮需要同步置灰。
     */
    const renderRightContent = () => {
      const singleHasResult = mode === 'single' && !!singleResultImage;
      const isEnhanceProcessing = singleEraserLoading || batchEraserLoading;
      const previewBatchItem = isPreviewMode && previewImageIndex !== null ? batchImages[previewImageIndex] : undefined;
      const batchActionProcessing = mode === 'batch' && (
        isPreviewMode
          ? previewBatchItem?.status === 'processing'
          : batchProcessingMode && batchImages.some((img) => img.selected && img.status === 'processing')
      );
      const isResultActionDisabled = isEnhanceProcessing || batchActionProcessing;
      // 批量处理中未勾选任何图片时，禁用走批量选中列表的操作（下载在大图预览时仍针对当前张）
      const batchNoSelection = batchProcessingMode && selectedCount === 0;
      return (
        <div className={styles.renderRightContent}>
          {/* 对比按钮 */}
          {((singleHasResult && singleMediaType !== 'video')
            || (isPreviewMode && !!batchImages[previewImageIndex]?.resultUrl && batchImages[previewImageIndex]?.type !== FileTypeEnum.Video)) && (
            <Button
              type="plain"
              className={styles.rightContentButton}
              disabled={isEnhanceProcessing}
              onLongPress={handleCompare}
              onLongPressEnd={handleCompare}
              longPressDelay={0}
            >
              <IconFont
                name="symmetryHorizontalBold"
                className={styles.rightContentButtonIcon}
                fontSize={16}
                color={isEnhanceProcessing ? 'currentColor' : 'var(--content-buttonSecondary)'}
              />
              {languageStore.smartcatTranslationText((v) => v.compare)}
            </Button>
          )}

          {/* 取消选择按钮 */}
          {batchProcessingMode && !isPreviewMode && (
            <Button
              type="plain"
              className={styles.rightContentButton}
              onClick={cancelBatchSelection}
            >
              {languageStore.smartcatTranslationText((v) => v.asset_unselect)}
            </Button>
          )}

          {/* 批量处理 */}
          {mode === 'batch'
            && !batchProcessingMode
            && !isPreviewMode
            && batchImages.length > 0 && (
              <Button
                type="plain"
                className={styles.rightContentButton}
                onClick={() => setBatchProcessingMode(true)}
              >
                {languageStore.smartcatTranslationText((v) => v.batched_processing)}
              </Button>
          )}

          {/*
          添加到资产库入口临时下线，仅隐藏入口，保留入库逻辑便于恢复。
          {(singleHasResult || isPreviewMode || batchProcessingMode) && (
            <Button
              type="plain"
              className={styles.rightContentButton}
              disabled={isResultActionDisabled || isAddingAsset || (batchNoSelection && !isPreviewMode)}
              onClick={handleAddToAssetLibrary}
            >
              {isAddingAsset && (
                <IconFont
                  name="loadingCircleBold"
                  fontSize={16}
                  className={`${styles.rightContentButtonIcon} ${styles.loadingIcon}`}
                />
              )}
              {languageStore.smartcatTranslationText((v) => v.add_to_assets)}
            </Button>
          )}
          */}

          {/* 在工作流中打开按钮 */}
          {(singleHasResult || batchProcessingMode) && (
            <Button
              type="plain"
              className={styles.rightContentButton}
              disabled={isEnhanceProcessing || batchNoSelection}
              onClick={openInWorkflow}
            >
              {languageStore.smartcatTranslationText((v) => v.open_in_workflow)}
            </Button>
          )}

          {/* 重新上传 */}
          {(mode === 'single'
            || (mode === 'batch' && !batchProcessingMode && !isPreviewMode)) && (
            <Button
              type="plain"
              className={styles.rightContentButton}
              disabled={singleEraserLoading || batchEraserLoading}
              onClick={handleReUpload}
            >
              <IconFont
                name="plusBold"
                className={styles.rightContentButtonIcon}
                fontSize={16}
                color="var(--content-buttonSecondary)"
              />
              {languageStore.smartcatTranslationText(
                (v) => v.roboneo_web_reupload,
              )}
            </Button>
          )}

          {/* 下载 */}
          {(singleHasResult || isPreviewMode || batchProcessingMode) && (
            <Button
              type="primary"
              className={styles.rightContentButton}
              disabled={isResultActionDisabled || isDownloading || (batchNoSelection && !isPreviewMode)}
              onClick={downloadHandler}
            >
              {isDownloading
                ? (
                  <IconFont
                    name="loadingCircleBold"
                    fontSize={16}
                    className={`${styles.rightContentButtonIcon} ${styles.loadingIcon}`}
                  />
                )
                : (
                  <IconFont
                    name="downloadBold"
                    className={styles.rightContentButtonIcon}
                    fontSize={16}
                    color="var(--content-buttonPrimaryAccent)"
                  />
                )}
              {languageStore.smartcatTranslationText(
                (v) => v.roboneo_web_download,
              )}
            </Button>
          )}
        </div>
      );
    };

    // 判断当前应展示的图片（对比模式时切换原图/结果图）
    const displayImageUrl = singleCompareIng
      ? singleOriginalImage
      : singleResultImage || singleOriginalImage;
    const singleAutoFitKey = singleResultImage || singleOriginalImage;

    /**
     * 结果媒体真正写入画布后再释放顶部按钮禁用态，避免画布 loading 仍在但操作已放开。
     */
    const handleSingleCanvasMediaLoaded = useCallback((items: MediaItem[]) => {
      const loadedUrl = items[0]?.url;
      if (mode !== 'single' || !singleEraserLoading || !singleResultImage) return;
      if (loadedUrl !== singleResultImage) return;
      setSingleEraserLoading(false);
    }, [mode, singleEraserLoading, singleResultImage]);

    // 单张模式是否展示画布/视频区域
    const showCanvas = !!singleOriginalImage;

    // 单张画布可能在批量模式隐藏期间完成加载，回到可见状态后需要等布局恢复再按真实容器尺寸适配。
    useEffect(() => {
      if (mode !== 'single' || !showCanvas || !singleAutoFitKey) return undefined;
      let secondFrameId = 0;
      const firstFrameId = window.requestAnimationFrame(() => {
        secondFrameId = window.requestAnimationFrame(() => {
          handleFitScreen();
        });
      });
      return () => {
        window.cancelAnimationFrame(firstFrameId);
        if (secondFrameId) window.cancelAnimationFrame(secondFrameId);
      };
    }, [mode, showCanvas, singleAutoFitKey]);

    const singleContent = (
      <>
        {showCanvas && (
          <div className={styles.canvas_wrapper}>
            <AIToolsInfiniteCanvas
              imageUrl={displayImageUrl}
              mediaType={singleMediaType}
              autoFitKey={singleAutoFitKey}
              onMediaLoaded={handleSingleCanvasMediaLoaded}
            />
            <CanvasToolbar />
          </div>
        )}
        <div
          className={
            singleOriginalImage
              ? styles.hiddenUploader
              : styles.singleUploadArea
          }
        >
          <div className={styles.singleUploadCard}>
            <ImageUploader
              onFilesSelected={(files) => {
                const validFiles = validateFiles(files);
                if (validFiles.length) {
                  if (!singleOriginalImage && !singleUploadTrackSourceRef.current) {
                    singleUploadTrackSourceRef.current = 'canvas';
                  }
                  handleSingleUpload(validFiles);
                }
              }}
              beforeOpen={guardUploadEntry}
              onOpen={handleSingleUploadOpen}
              maxCount={1}
              currentCount={0}
              disabled={uploading}
              accept={ALL_ACCEPT}
              openKey={singleReuploadSignal}
              title={languageStore?.smartcatTranslationText((v) => v.drag_and_drop_the_image_video_here_or_click_upload)}
              description={languageStore?.smartcatTranslationText((v) => v.images_support_jpg_jpeg_png_and_video_support_mp4_and_mov)}
            />
          </div>
        </div>
      </>
    );

    return (
      <>
        {/* 视频播放控制条 */}
        {singleMediaType === 'video' && mode === 'single' && !singleEraserLoading && <VideoBar />}
        {/* Header */}
        <AIToolsHeader
          title={languageStore.smartcatTranslationText((v) => v.enhance_title)}
          AIToolsHeaderCenterContent={renderCenterContent()}
          AIToolsHeaderRightContent={renderRightContent()}
        />
        {/* 左侧菜单 */}
        <LeftPanel uploading={uploading} />
        {/* 内容: 切换批量/单张时 隐藏/显示，不销毁，避免画布状态丢失 */}
        <div style={{ display: mode === 'single' ? 'contents' : 'none' }}>
          {singleContent}
        </div>
        <div style={{ display: mode === 'batch' ? 'contents' : 'none' }}>
          <BatchList />
          <BatchImagePreview />
        </div>
      </>
    );
  },
});
